/**
 * The text comms panel's pure model (M7 plan S31; spec §2.7, §3.1; integration notes S11).
 *
 * - **Hails:** a story `comms` effect (a node) or a comms-channel `callback` (a one-line hail) waits in a queue
 *   until the player answers it. A hail **never times out**. The same node or callback is never queued twice.
 * - **Session:** answering takes the oldest hail onto the panel. A reply goes through the story port (`choose`):
 *   the panel follows `next`, or the conversation ends. Closing the panel without a reply (Esc, B) puts the node
 *   back at the front of the queue, so a conversation is never lost; a one-line hail is read once shown.
 * - **Panel model:** the speaker (name, code-drawn placeholder portrait: M6's seeded silhouette, initials and the
 *   faction tint), the node's lines and its 2–4 replies, numbered, with "Show [log]" replies marked.
 *
 * Event rate (answers, picks, effects); `hailPromptText` runs only when the prompt changes.
 */
import { z } from 'zod'
import { commsConfig, type CommsView } from '../../story/comms'
import type { Speaker } from '../../story/types'
import type { FactionId } from '../../strategic/types'
import { loadCaptainsConfig } from '../../ui/fleet/models/captains'
import { hashSeed, portraitRecipe, type PortraitRecipe, type PortraitTint } from '../../ui/fleet/models/portrait'
import { keyLabel } from '../fleetFeed/model'
import type { CommsStory, HailEffect } from './port'
import raw from './comms.json'

const tintSchema = z.strictObject({ bg: z.string().min(1), fg: z.string().min(1) })

const configSchema = z.strictObject({
  comment: z.string().optional(),
  stress: z.number().min(0).max(1),
  lineGapS: z.number().nonnegative(),
  neutralTint: tintSchema,
  labels: z.strictObject({
    title: z.string().min(1),
    tab: z.string().min(1),
    hail: z.string().includes('{name}'),
    answer: z.string().min(1),
    unknownCaller: z.string().min(1),
    show: z.string().min(1),
    shows: z.string().includes('{log}'),
    endChannel: z.string().min(1),
    noHail: z.string().min(1),
  }),
})
export type CommsUiConfig = z.infer<typeof configSchema>
export type CommsLabels = CommsUiConfig['labels']

export function loadCommsUiConfig(data: unknown = raw): CommsUiConfig {
  return configSchema.parse(data)
}

// -------------------------------------------------------------------------------------------------
// Speakers
// -------------------------------------------------------------------------------------------------

/** A speaker's display data (S9 `comms/common.json speakers`; names are placeholders until the naming pass). */
export interface SpeakerInfo {
  name: string
  faction: FactionId | null
  portrait: string
}

/** Looks up a speaker's display data; null for a callback without a speaker. */
export type SpeakerLookup = (speaker: Speaker) => SpeakerInfo

export function storySpeakers(): SpeakerLookup {
  const speakers = commsConfig().speakers
  return (s) => {
    const who = speakers[s]
    return { name: who.name, faction: who.faction ?? null, portrait: who.portrait }
  }
}

/** Portrait tints by faction (the captains', so a story speaker and a captain of one faction share a tint). */
export type TintLookup = (faction: FactionId | null) => PortraitTint

export function factionTints(cfg: CommsUiConfig, tints: Readonly<Record<FactionId, PortraitTint>> = loadCaptainsConfig().tints): TintLookup {
  return (f) => (f === null ? cfg.neutralTint : tints[f])
}

/** The placeholder portrait of a speaker: M6's recipe seeded by the name and the portrait id. Deterministic. */
export function speakerPortrait(name: string, portraitId: string, tint: PortraitTint): PortraitRecipe {
  return portraitRecipe(name, hashSeed(portraitId), tint)
}

/** A speaker's babble voice seed (stable per speaker; `src/audio/comms` picks the formants from it). */
export function voiceSeed(portraitId: string): number {
  return hashSeed(`voice|${portraitId}`)
}

// -------------------------------------------------------------------------------------------------
// Hails and the session
// -------------------------------------------------------------------------------------------------

export type Hail =
  | { kind: 'node'; key: string; speaker: Speaker; nodeId: string }
  | { kind: 'line'; key: string; speaker: Speaker | null; callbackId: string; text: string }

export function nodeHail(speaker: Speaker, nodeId: string): Hail {
  return { kind: 'node', key: `node:${nodeId}`, speaker, nodeId }
}

export function hailOf(e: HailEffect): Hail {
  if (e.type === 'comms') return nodeHail(e.speaker, e.nodeId)
  return { kind: 'line', key: `cb:${e.callbackId}`, speaker: e.speaker ?? null, callbackId: e.callbackId, text: e.text }
}

/** The reply id that closes a one-line hail (never a story reply id: those are data ids without a colon). */
export const CLOSE_REPLY = ':close'

export interface CommsSession {
  /** Waiting hails, oldest first. */
  readonly queue: Hail[]
  /** The hail on the panel, null when the panel shows none. */
  open: Hail | null
  /** The node on the panel (a node hail's current node), null otherwise. */
  nodeId: string | null
  /** Bumped on every change (the HUD prompt and the panel re-read on change only). */
  version: number
}

export function createCommsSession(): CommsSession {
  return { queue: [], open: null, nodeId: null, version: 0 }
}

/** Queues a hail unless the same node or callback already waits or is on the panel. True when queued. */
export function enqueueHail(s: CommsSession, h: Hail): boolean {
  if (s.queue.some((q) => q.key === h.key)) return false
  if (s.open !== null && (s.open.key === h.key || (h.kind === 'node' && s.nodeId === h.nodeId))) return false
  s.queue.push(h)
  s.version++
  return true
}

/** The hail the HUD prompt offers (the oldest), null when none waits or the panel is open. */
export function waitingHail(s: Readonly<CommsSession>): Hail | null {
  return s.open === null && s.queue.length > 0 ? s.queue[0] : null
}

/** Answers the oldest waiting hail onto the panel. False when the panel is busy or nothing waits. */
export function answerHail(s: CommsSession): boolean {
  if (s.open !== null || s.queue.length === 0) return false
  const h = s.queue.shift() as Hail
  s.open = h
  s.nodeId = h.kind === 'node' ? h.nodeId : null
  s.version++
  return true
}

/** What a pick did: `moved` (the next node), `ended`, or `refused` (nothing changed). */
export type ReplyOutcome = 'moved' | 'ended' | 'refused'

/**
 * Dispatches a reply: a node's reply goes to the story port (`choose`); the next node replaces the panel's (and a
 * queued hail for it is dropped: the story's own `comms` effect for `next`), or the conversation ends. A one-line
 * hail takes only `CLOSE_REPLY`, which ends any conversation without a story pick.
 */
export function chooseReply(s: CommsSession, story: Pick<CommsStory, 'choose'> | null, replyId: string): ReplyOutcome {
  const h = s.open
  if (h === null) return 'refused'
  // Close: the one-line hail's only reply, and an unknown node's (stale data) way out.
  if (replyId === CLOSE_REPLY) {
    endConversation(s)
    return 'ended'
  }
  if (h.kind === 'line' || s.nodeId === null || story === null) return 'refused'
  const next = story.choose(s.nodeId, replyId)
  if (next === undefined) return 'refused'
  if (next === null) {
    endConversation(s)
    return 'ended'
  }
  s.nodeId = next
  const i = s.queue.findIndex((q) => q.kind === 'node' && q.nodeId === next)
  if (i >= 0) s.queue.splice(i, 1)
  s.version++
  return 'moved'
}

function endConversation(s: CommsSession): void {
  s.open = null
  s.nodeId = null
  s.version++
}

/**
 * Closes the panel without a reply (Esc, B, Close). A node conversation goes back to the front of the queue at its
 * current node (it never times out); a one-line hail has been read and is gone.
 */
export function closePanel(s: CommsSession): void {
  const h = s.open
  if (h === null) return
  if (h.kind === 'node' && s.nodeId !== null) s.queue.unshift(nodeHail(h.speaker, s.nodeId))
  endConversation(s)
}

// -------------------------------------------------------------------------------------------------
// The panel and the prompt
// -------------------------------------------------------------------------------------------------

export interface PanelReply {
  id: string
  /** 1-based; the number key that picks it. */
  n: number
  text: string
  /** A "Show [log]" reply. */
  show: boolean
  /** "Shows: <log title>" for a Show reply whose log has a title, else null. */
  shows: string | null
}

export interface PanelModel {
  /** Changes whenever the panel shows a different node or hail (React key, the babble). */
  key: string
  name: string
  faction: FactionId | null
  portrait: PortraitRecipe
  voice: number
  lines: readonly string[]
  replies: PanelReply[]
}

export interface PanelDeps {
  story: Pick<CommsStory, 'view' | 'logTitle'> | null
  speakers: SpeakerLookup
  tints: TintLookup
  labels: CommsLabels
}

function speakerOf(speaker: Speaker | null, deps: PanelDeps): SpeakerInfo {
  if (speaker !== null) return deps.speakers(speaker)
  return { name: deps.labels.unknownCaller, faction: null, portrait: 'portrait.unknown' }
}

function replyRows(view: CommsView, deps: PanelDeps): PanelReply[] {
  return view.replies.map((r, i) => {
    const title = r.showLog === undefined ? null : (deps.story?.logTitle?.(r.showLog) ?? null)
    return {
      id: r.id,
      n: i + 1,
      text: r.text,
      show: r.showLog !== undefined,
      shows: title === null ? null : deps.labels.shows.replace('{log}', title),
    }
  })
}

/** What the panel draws now; null when the panel shows no hail (the empty Comms tab). */
export function panelModel(s: Readonly<CommsSession>, deps: PanelDeps): PanelModel | null {
  const h = s.open
  if (h === null) return null
  if (h.kind === 'line') {
    const who = speakerOf(h.speaker, deps)
    return {
      key: h.key,
      name: who.name,
      faction: who.faction,
      portrait: speakerPortrait(who.name, who.portrait, deps.tints(who.faction)),
      voice: voiceSeed(who.portrait),
      lines: [h.text],
      replies: [{ id: CLOSE_REPLY, n: 1, text: deps.labels.endChannel, show: false, shows: null }],
    }
  }
  const nodeId = s.nodeId ?? h.nodeId
  const view = deps.story?.view(nodeId) ?? null
  // The view carries the node's own portrait id (a node may override the speaker's default).
  const who = view === null ? speakerOf(h.speaker, deps) : { name: view.name, faction: view.faction ?? null, portrait: view.portrait }
  return {
    key: `${h.key}>${nodeId}`,
    name: who.name,
    faction: who.faction,
    portrait: speakerPortrait(who.name, who.portrait, deps.tints(who.faction)),
    voice: voiceSeed(who.portrait),
    lines: view?.lines ?? [],
    // An unknown node (stale save data) still closes: the panel offers only Close.
    replies: view === null ? [{ id: CLOSE_REPLY, n: 1, text: deps.labels.endChannel, show: false, shows: null }] : replyRows(view, deps),
  }
}

/** The HUD prompt's text for a waiting hail: "<name> is hailing · [O] Answer". Built on change only. */
export function hailPromptText(h: Hail, speakers: SpeakerLookup, labels: CommsLabels, keys: readonly string[]): string {
  const name = h.speaker === null ? labels.unknownCaller : speakers(h.speaker).name
  const head = labels.hail.replace('{name}', name)
  return keys.length === 0 ? head : `${head} · [${keyLabel(keys[0])}] ${labels.answer}`
}

/** A number key code ("Digit3", "Numpad3") → reply number 1..9, else 0. */
export function replyNumberOf(code: string): number {
  const d = code.startsWith('Digit') ? code.slice(5) : code.startsWith('Numpad') ? code.slice(6) : ''
  return d.length === 1 && d >= '1' && d <= '9' ? Number(d) : 0
}
